import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetSidebarState } from "../redux/sidebarSlice";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import api from "../api";
import "../css/Profile.css";
import { setSessionId } from "../redux/sessionSlice";
import { triggerRefresh } from "../redux/refreshSlice";
import { setSelectedModel } from "../redux/modelSlice";
import { branding } from "../utils/branding";
import { useProfiles } from "../profile/useProfiles";
import { syncProfilesFromResponse } from "../profile/profileSync";

import { FaEye, FaEyeSlash } from "react-icons/fa";
import DiscardIcon from "../assets/UserIcons/Cancel.svg";
import EditIcon from "../assets/ProfileSettingIcons/EditIconBlack.svg";
import RedLogoutIcon from "../assets/AdminIcon 2/LogoutIconBlack.svg";
import CameraIcon from "../assets/icons/profile_camera.png";
import LocationIcon from "../assets/ProfileSettingIcons/LocationIconBlack.svg";
import EmailIcon from "../assets/ProfileSettingIcons/EmailIconBlack.svg";
import EnterpriseIcon from "../assets/AdminIcon 2/EnterprizeIconBlack.svg";
import UserIcon from "../assets/ProfileSettingIcons/UserIdIcon.svg";
import { formatDateToUserTimeZone } from "../utils/formatDate";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UserProfileSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile, sessionProfile } = useProfiles();
  const [user, setUser] = useState({
    user_profile_pic: "",
    name: "",
    email: "",
    login_name: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activitySessions, setActivitySessions] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [nameError, setNameError] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [logoutState, setLogoutState] = useState("Logout");

  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [userRole, setUserRole] = useState(null);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  const sessionId = useSelector((state) => state.session.sessionId);

  const [isEditingProfileInfo, setIsEditingProfileInfo] = useState(false);
  const [isSavingProfileInfo, setIsSavingProfileInfo] = useState(false);
  const [profileInfoDraft, setProfileInfoDraft] = useState({
    role: "",
    expertise_level: "",
    overall_profile_summary: "",
  });

  useEffect(() => {
    if (isEditingProfileInfo) return;
    setProfileInfoDraft({
      role: userProfile?.role || "",
      expertise_level: userProfile?.expertise_level || "",
      overall_profile_summary:
        userProfile?.chat_preferences?.overall_profile_summary || "",
    });
  }, [isEditingProfileInfo, userProfile]);

  useEffect(() => {
    if (user) {
      setUserRole(user.role_name || "User");
    }
  }, [user]);

  const validateProfileInfoDraft = (draft) => {
    const role = String(draft?.role ?? "").trim();
    const expertiseLevel = String(draft?.expertise_level ?? "").trim();
    const summary = String(draft?.overall_profile_summary ?? "").trim();

    if (role.length > 120) return "Role must be 120 characters or less.";
    if (expertiseLevel.length > 60)
      return "Expertise level must be 60 characters or less.";
    if (summary.length > 8000)
      return "Profile summary must be 8000 characters or less.";

    return null;
  };

  const handleSaveProfileInfo = async () => {
    const errorMsg = validateProfileInfoDraft(profileInfoDraft);
    if (errorMsg) {
      showErrorToast(errorMsg);
      return;
    }

    const role = String(profileInfoDraft.role || "").trim();
    const expertiseLevel = String(profileInfoDraft.expertise_level || "").trim();
    const summary = String(profileInfoDraft.overall_profile_summary || "").trim();

    if (!role && !expertiseLevel && !summary) {
      showInfoToast("Nothing to save");
      return;
    }

    setIsSavingProfileInfo(true);
    try {
      const payload = {
        session_id: sessionId || null,
        role: role || null,
        expertise_level: expertiseLevel || null,
        chat_preferences: {
          overall_profile_summary: summary || null,
        },
      };

      const res = await api.put(`/user-profile/ai-profile`, payload, {
        skipLoader: false,
      });

      syncProfilesFromResponse(res?.data || {});
      showSuccessToast("Profile updated");
      setIsEditingProfileInfo(false);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to update profile";
      showErrorToast(message);
    } finally {
      setIsSavingProfileInfo(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const [isEditingUserName, setIsEditingUserName] = useState(false);
  const userNameInputRef = useRef(null);

  useEffect(() => {
    if (isEditingUserName && userNameInputRef.current) {
      userNameInputRef.current.focus(); // ⬅️ auto-focus input when editing starts
    }
  }, [isEditingUserName]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const [toggles, setToggles] = useState({
    notificationsAll: true,
    messageAlerts: true,
    sessionReminders: true,
    feedbackRequest: true,
    fontSize: false,
    autoScroll: false,
    botLang: false,
  });

  const [preferences, setPreferences] = useState({
    fontSize: "Medium",
    botLang: "English",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/notification-settings`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Normalize backend -> frontend
        setToggles((prev) => ({
          ...prev,
          notificationsAll: data.notifications_all ?? true,
          messageAlerts: data.message_alerts ?? true,
          sessionReminders: data.session_reminders ?? true,
          feedbackRequest: data.feedback_request ?? true,
        }));
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
    };

    fetchSettings();
  }, []);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/auth/activity`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // ✅ Store sessions data from response
      setActivitySessions(data.sessions || []);

      // console.log("Fetched activity sessions:", data.sessions);
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [token]);

  const handleLogoutDevice = async (session_id) => {
    try {
      // Call API to logout single device
      await api.post(
        `${process.env.REACT_APP_API_URL}/auth/logout-device`,
        { session_id }, // ✅ Send correct session ID
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // ✅ Fetch updated session data
      fetchActivity();
    } catch (err) {
      console.error("Logout device failed:", err);
    }
  };

  // Fetch user preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const prefs = response.data.preferences;

        setToggles({
          notificationsAll: prefs.notifications.notifications_all,
          messageAlerts: prefs.notifications.message_alerts === "Instant",
          sessionReminders: prefs.notifications.session_reminders,
          feedbackRequest: prefs.notifications.feedback_request || true,
          autoScroll: prefs.chat.auto_scroll,
        });

        setPreferences({
          fontSize: prefs.chat.font_size,
          botLang: prefs.chat.bot_language,
        });
      } catch (error) {
        console.error("Error fetching preferences:", error);
      }
    };

    fetchPreferences();
  }, [token]);

  const handleDropdownChange = async (key, value) => {
    const updatedPrefs = { ...preferences, [key]: value };
    setPreferences(updatedPrefs);

    await savePreferences({ ...toggles, ...updatedPrefs });
  };

  const handleToggle = async (key) => {
    const updatedToggles = { ...toggles, [key]: !toggles[key] };
    setToggles(updatedToggles);

    await savePreferences({ ...updatedToggles, ...preferences });
  };

  const savePreferences = async (combined) => {
    try {
      const payload = {
        preferences: {
          notifications: {
            notifications_all: combined.notificationsAll,
            message_alerts: combined.messageAlerts ? "Instant" : "Off",
            session_reminders: combined.sessionReminders,
          },
          chat: {
            font_size: combined.fontSize,
            auto_scroll: combined.autoScroll,
            bot_language: combined.botLang,
          },
        },
      };

      const response = await api.put(
        `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      dispatch(triggerRefresh());
      showSuccessToast(response.data.message);
    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const handleSave = async () => {
    try {
      // 1. Password update (only if new + confirm fields are filled)
      if (formData.newPassword && formData.confirmPassword) {
        const passwordResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/user-profile/change-password`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              newPassword: formData.newPassword,
              confirmPassword: formData.confirmPassword,
            }),
          },
        );

        const passwordData = await passwordResponse.json();

        if (!passwordResponse.ok) {
          showErrorToast(passwordData.message || "Failed to update password");
          return;
        }

        // ✅ Password updated successfully → reset fields
        setFormData({
          ...formData,
          newPassword: "",
          confirmPassword: "",
        });

        showSuccessToast("Password changed successfully");
      }

      // Final success for other updates
      // showSuccessToast("Profile updated successfully");
      setIsEditing(false);
    } catch (err) {
      console.error("Save error:", err);
      showErrorToast("Failed to save changes");
    }
  };

  useEffect(() => {
    if (token) fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    const token1 = token;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user-profile/profile`,
        { headers: { Authorization: `Bearer ${token1}` } },
      );
      if (!res.ok) throw new Error("Failed to fetch profile");

      const data = await res.json();

      // console.log(data)

      setUser(data);

      // ✅ Update only name and profile pic in localStorage & sessionStorage
      const storedUserLocal = JSON.parse(localStorage.getItem("user")) || {};
      const storedUserSession =
        JSON.parse(sessionStorage.getItem("user")) || {};

      const updatedUser = {
        ...storedUserLocal,
        user_name: data.user_name,
        user_profile_pic: data.user_profile_pic,
      };

      // console.log(data.user_name,"jhghdgdsk")

      localStorage.setItem("user", JSON.stringify(updatedUser));
      sessionStorage.setItem(
        "user",
        JSON.stringify({
          ...storedUserSession,
          user_name: data.user_name,
          user_profile_pic: data.user_profile_pic,
        }),
      );

      // console.log(localStorage.getItem("user"))

      // ✅ Trigger refresh for Topbar
      dispatch(triggerRefresh());
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.PROFILE.FETCH_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      showErrorToast(TOAST_MESSAGES.PROFILE.IMAGE_TOO_LARGE);
      return;
    }

    const formData = new FormData();
    formData.append("profileImage", file);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user-profile/upload-profile-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      if (!res.ok) throw new Error("Image upload failed");

      const data = await res.json();

      setUser((prevUser) => {
        const updatedUser = {
          ...(prevUser || {}),
          user_profile_pic:
            data?.profileImagePath || prevUser?.user_profile_pic || "",
        };

        // ✅ Update localStorage & sessionStorage
        const storedUserLocal = JSON.parse(localStorage.getItem("user")) || {};
        const storedUserSession =
          JSON.parse(sessionStorage.getItem("user")) || {};

        const updatedStoredUserLocal = {
          ...storedUserLocal,
          user_profile_pic: updatedUser.user_profile_pic,
        };
        localStorage.setItem("user", JSON.stringify(updatedStoredUserLocal));

        const updatedStoredUserSession = {
          ...storedUserSession,
          user_profile_pic: updatedUser.user_profile_pic,
        };
        sessionStorage.setItem(
          "user",
          JSON.stringify(updatedStoredUserSession),
        );

        return updatedUser;
      });

      // ✅ Trigger refresh for Topbar
      dispatch(triggerRefresh());
      showSuccessToast(TOAST_MESSAGES.PROFILE.IMAGE_UPLOAD_SUCCESS);
    } catch (err) {
      console.error("Upload error:", err);
      showErrorToast(TOAST_MESSAGES.PROFILE.IMAGE_UPLOAD_FAILED);
    }
  };

  useEffect(() => {
    if (token) fetchChatSessions();
  }, [token, isFullPrivacy]);

  const fetchChatSessions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat/sessions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        let sessions = data.sessions || [];

        // ✅ If full privacy mode is ON, keep only "Offline" sessions
        if (isFullPrivacy) {
          sessions = sessions.filter(
            (session) =>
              session.session_type === "Offline" &&
              session.mode !== "incognito",
          );
        } else {
          sessions = sessions.filter(
            (session) =>
              session.session_type === "Online" && session.mode !== "incognito",
          );
        }

        setChatSessions(sessions);
      } else {
        console.error("❌ Failed to load sessions");
      }
    } catch (err) {
      console.error("❌ Error fetching sessions:", err);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000); // minutes

    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        {/* <div className="spinner-border textblack" role="status">
          <span className="visually-hidden">Loading...</span>
        </div> */}
      </div>
    );
  }

  if (error) return <div className="container py-5 text-danger">{error}</div>;

  const profileImage = user?.user_profile_pic
    ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
    : null;

  return (
    <div
      className="container-fluid profile-container"
      style={{ paddingBottom: "20px" }}
    >
      {!isEditing ? (
        <div className="card profile p-4 rounded-4 border-1 mb-1">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h4
              className="fw-bold mb-0 refresh-prompts"
              style={{ fontSize: "large" }}
            >
              User Profile
            </h4>
            <img
              src={EditIcon}
              alt="Location"
              className="me-1 icon-image"
              style={{
                fontSize: "18px",
                cursor: "pointer",
                width: "20px",
                height: "20px",
              }}
              onClick={() => setIsEditing(true)}
            />
          </div>

          <div className="d-flex flex-column flex-md-row align-items-center gap-4">
            {/* Profile Image */}
            {user.user_profile_pic ? (
              <img
                src={`${SERVER_URL}/${user.user_profile_pic.replace(
                  /\\/g,
                  "/",
                )}`}
                alt="Profile"
                className="rounded-circle profile-image"
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  cursor: "pointer",
                }}
              />
            ) : (
              <div
                className="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center"
                style={{
                  width: "80px",
                  height: "80px",
                  fontSize: "28px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {user.user_name?.charAt(0).toUpperCase() || "U"}
              </div>
            )}

            {/* Info Section */}
            <div className="w-100">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3 info-section">
                <div>
                  <h5 className="mb-1 fw-semibold fs-6">
                    {user.user_name
                      ? user.user_name.charAt(0).toUpperCase() +
                        user.user_name.slice(1)
                      : "—" || "Unnamed User"}
                  </h5>
                  <p
                    className="mb-0 refresh-prompts"
                    style={{ fontSize: "14px" }}
                  >
                    Last Login:{" "}
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div
                className="d-flex flex-wrap align-items-center justify-content-start text-muted gap-5 info-details"
                style={{ fontSize: "15px" }}
              >
                {/* <div className="d-flex refresh-prompts align-items-center">
                  <img
                    src={LocationIcon}
                    alt="Location"
                    className="me-2 profile-i"
                  />
                  {user.country || "India"}
                </div>
                <div className="d-flex refresh-prompts align-items-center">
                  <img src={EmailIcon} alt="Email" className="me-2 profile-i" />
                  {user.user_email || "John123@gmail.com"}
                </div>
                <div className="d-flex  refresh-prompts align-items-center">
                  <img
                    src={EnterpriseIcon}
                    alt="Enterprize"
                    className="me-2 profile-i"
                  />
                  {user.user_enterprise || "NextAstra"}
                </div>
                <div className="d-flex refresh-prompts align-items-center">
                  <img
                    src={UserIcon}
                    alt="User Role"
                    className="me-2 profile-i"
                  />
                  {user.role_name || "User"}
                </div> */}
                <div className="d-flex refresh-prompts align-items-center">
                  {/* <i className="bi bi-geo-alt profile-i me-2 fs-5"></i> */}
                  <img
                    src={LocationIcon}
                    className="icon-image me-2"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                    alt="Location"
                  />
                  {user.country || "India"}
                </div>

                {user?.login_name?.trim() && (
                  <div className="d-flex refresh-prompts align-items-center">
                    <i className="bi bi-person profile-i me-2 fs-5"></i>
                    {user.login_name}
                  </div>
                )}

                <div className="d-flex refresh-prompts align-items-center">
                  {/* <i className="bi bi-envelope profile-i me-2 fs-5"></i> */}
                  <img
                    src={EmailIcon}
                    className="icon-image me-2"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                    alt="Location"
                  />
                  {user.user_email || "John123@gmail.com"}
                </div>

                <div className="d-flex refresh-prompts align-items-center">
                  {/* <i className="bi bi-building profile-i me-2 fs-5"></i> */}
                  <img
                    src={EnterpriseIcon}
                    className="icon-image me-2"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                    alt="Location"
                  />
                  {user.user_enterprise || "NextAstra"}
                </div>

                <div className="d-flex refresh-prompts align-items-center">
                  {/* <i className="bi bi-person-badge profile-i me-2 fs-5"></i> */}
                  <img
                    src={UserIcon}
                    className="icon-image me-2"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                    alt="Location"
                  />
                  {user.role_name || "User"}
                </div>

                <div className="d-flex align-items-center">
                  <span
                    className={`me-2 ${
                      user.user_status === "1" ? "text-success" : "text-danger"
                    }`}
                    style={{ fontSize: "20px" }}
                  >
                    ●
                  </span>
                  <span
                    className={
                      user.user_status === "1"
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {user.user_status === "1" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card profile p-4 rounded-4 border-1">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="fw-bold mb-0" style={{ fontSize: "22px" }}>
              Edit Personal Details
            </h4>
            <span
              onClick={() => setIsEditing(false)}
              style={{
                cursor: "pointer",
                fontSize: "24px",
                lineHeight: 1,
              }}
              title="Close"
            >
              <img
                src={DiscardIcon}
                className="icon-image"
                style={{
                  width: "20px",
                  height: "20px",
                }}
                alt="Close"
              />
            </span>
          </div>
          <div className="row">
            {/* Profile Picture Section (Left) */}
            <div className="col-md-4 d-flex flex-column align-items-center justify-content-center">
              <div className="position-relative mb-3">
                {selectedImage || profileImage ? (
                  // ✅ If profile picture exists → show image
                  <img
                    src={selectedImage || profileImage}
                    alt="Profile"
                    className="rounded-circle border"
                    style={{
                      width: "140px",
                      height: "140px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  // ✅ If no profile picture → show first name initial
                  <div
                    className="rounded-circle border d-flex align-items-center justify-content-center bg-dark text-white"
                    style={{
                      width: "140px",
                      height: "140px",
                      fontSize: "48px",
                      fontWeight: "600",
                    }}
                  >
                    {user?.user_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}

                {/* Upload icon overlay */}
                <label
                  htmlFor="profilePicUpload"
                  className="profile-pic-upload-icon position-absolute bottom-0 start-50 translate-middle-x bg-white border rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    transform: "translate(-50%, 50%)",
                    cursor: "pointer",
                    width: "40px",
                    height: "40px",
                  }}
                >
                  <img
                    src={CameraIcon}
                    alt="Camera"
                    style={{ width: "20px", height: "20px" }}
                  />
                </label>
              </div>

              {/* CHOOSE FILE Button */}
              <label
                htmlFor="profilePicUpload"
                className="btn custom-btn fw-bold px-4 py-2 mb-2 rounded-pill"
                style={{ fontSize: "16px", borderWidth: "2px" }}
              >
                CHOOSE FILE
              </label>
              <input
                id="profilePicUpload"
                type="file"
                className="d-none"
                onChange={handleImageUpload}
              />

              {/* File Info */}
              <div
                className="refresh-prompts text-center mt-1"
                style={{ fontSize: "14px" }}
              >
                No file selected <br />
                Maximum image size is <strong>1 MB</strong>
              </div>
            </div>

            {/* Right side - Personal details */}
            <div className="col-md-8 mt-4 mt-md-0 personal-details">
              <div className="row">
                {/* First Name (Editable) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Full Name
                    <span className="text-danger"> *</span>
                  </label>
                  <div className="position-relative">
                    <input
                      ref={userNameInputRef}
                      type="text"
                      className={`form-control rounded-3 ps-3 pe-5 ${
                        (!user.user_name?.trim() && isEditingUserName) ||
                        nameError
                          ? "is-invalid"
                          : ""
                      }`}
                      value={user.user_name || ""}
                      readOnly={!isEditingUserName}
                      onClick={() => setIsEditingUserName(true)}
                      onChange={(e) => {
                        const value = e.target.value;

                        // ✅ Prevent invalid characters in real-time
                        const validPattern = /^[A-Za-z\s]*$/;
                        if (!validPattern.test(value)) {
                          showErrorToast(
                            "Name can only contain letters and spaces.",
                          );
                          return;
                        }

                        // ✅ Set value if valid
                        setUser({ ...user, user_name: value });
                        setNameError(false);
                      }}
                      onBlur={async () => {
                        if (!isEditingUserName) return;

                        const name = user.user_name?.trim() || "";
                        const nameRegex = /^[A-Za-z\s]{2,50}$/;

                        // ✅ Check for empty
                        if (!name) {
                          setNameError(true);
                          showErrorToast("User name cannot be blank.");
                          setIsEditingUserName(false);
                          return;
                        }

                        // ✅ Check format
                        if (!nameRegex.test(name)) {
                          setNameError(true);
                          showErrorToast(
                            "Please enter a valid name (only letters and spaces, 2–50 characters).",
                          );
                          setIsEditingUserName(false);
                          return;
                        }

                        const storedUser =
                          JSON.parse(localStorage.getItem("user")) ||
                          JSON.parse(sessionStorage.getItem("user")) ||
                          {};

                        // ✅ Skip if unchanged
                        if (name === storedUser.user_name) {
                          setIsEditingUserName(false);
                          return;
                        }

                        try {
                          const res = await fetch(
                            `${process.env.REACT_APP_API_URL}/user-profile/update`,
                            {
                              method: "PUT",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({ user_name: name }),
                            },
                          );

                          const data = await res.json();

                          if (!res.ok) {
                            showErrorToast(
                              data.message || "Failed to update user name.",
                            );
                          } else {
                            showSuccessToast("User name updated successfully.");

                            // ✅ Update local/session storage
                            const updatedStoredUser = {
                              ...storedUser,
                              user_name: name,
                            };
                            localStorage.setItem(
                              "user",
                              JSON.stringify(updatedStoredUser),
                            );
                            sessionStorage.setItem(
                              "user",
                              JSON.stringify(updatedStoredUser),
                            );

                            dispatch(triggerRefresh());
                          }
                        } catch (err) {
                          console.error("Update error:", err);
                          showErrorToast("Failed to update user name.");
                        } finally {
                          setIsEditingUserName(false);
                        }
                      }}
                      style={{
                        cursor: !isEditingUserName ? "pointer" : "text",
                      }}
                    />

                    {/* 🟢 Show Edit Icon only if no error and not editing */}
                    {user.user_name?.trim() &&
                      !nameError &&
                      !isEditingUserName && (
                        <img
                          src={EditIcon}
                          alt="edit"
                          className="position-absolute profile-i top-50 end-0 translate-middle-y me-3"
                          style={{ width: "16px", cursor: "pointer" }}
                          onClick={() => setIsEditingUserName(true)}
                        />
                      )}

                    {/* Validation Feedback */}
                    {((!user.user_name?.trim() && isEditingUserName) ||
                      nameError) && (
                      <div className="invalid-feedback">
                        Please enter a valid name (only letters and spaces
                        allowed).
                      </div>
                    )}
                  </div>
                </div>

                {/* Email (Read-only) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded-3 ps-3 pe-5"
                      value={user.user_email}
                      readOnly
                    />
                    {/* <img
                      src={EditIcon}
                      alt="edit"
                      className="position-absolute top-50 end-0 profile-i translate-middle-y me-3 "
                      style={{ width: "16px", pointerEvents: "none" }}
                    /> */}
                  </div>
                </div>

                {/* Enterprize (Read-only) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Enterprize Name
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded-3 ps-3 pe-5"
                      value={user.user_enterprise}
                      readOnly
                    />
                    {/* <img
                      src={EditIcon}
                      alt="edit"
                      className="position-absolute top-50 end-0 profile-i translate-middle-y me-3 "
                      style={{ width: "16px", pointerEvents: "none" }}
                    /> */}
                  </div>
                </div>
                {user?.login_name && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">User Name</label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control rounded-3 ps-3 pe-5"
                        value={user.login_name}
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row gx-3 gy-1">
        {/* Notification Preferences */}
        <div className="col-12 col-md-6 mb-4 d-flex profile_cards_spacing">
          <div className="card profile shadow-sm p-4 w-100 h-100">
            <h5 style={{ fontSize: "large" }} className="mb-4 fw-semibold">
              Notifications Preferences
            </h5>
            {[
              {
                key: "notificationsAll",
                title: "Enable All Notifications",
                description:
                  "Turn this on to receive all types of notifications.",
              },
              {
                key: "messageAlerts",
                title: "New Message Alerts",
                description: "Get notified when you receive new messages.",
              },
              {
                key: "sessionReminders",
                title: "Session Reminders",
                description: "Receive reminders before your sessions start.",
              },
            ].map(({ key, title, description }, index, arr) => (
              <div key={key}>
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <label
                      htmlFor={key}
                      className="form-label fw-semibold mb-1"
                    >
                      {title}
                    </label>
                    <p className="small mb-0">{description}</p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={key}
                      checked={toggles[key]}
                      onChange={() => handleToggle(key)}
                      style={{ width: "2.25em", height: "1.25em" }}
                    />
                  </div>
                </div>
                {index !== arr.length - 1 && <hr className="my-3" />}
              </div>
            ))}
          </div>
        </div>

        {/* Chat Preferences */}
        {userRole === "User" && (
          <div className="col-12 col-md-6 mb-4 d-flex preferences-card">
            <div className="card profile shadow-sm p-4 w-100 h-100">
              <h5 style={{ fontSize: "large" }} className="mb-4 fw-semibold">
                Chat Preferences
              </h5>

              {[
                {
                  key: "fontSize",
                  title: "Font Size",
                  description:
                    "Adjust the size of the chatbot text for better readability.",
                  type: "dropdown",
                  options: ["Small", "Medium", "Large"],
                },

                // {
                //   key: "botLang",
                //   title: branding("Language Of ZeroBox Response"),
                //   description:
                //     "Select the preferred language for the bot’s replies.",
                //   type: "dropdown",
                //   options: ["English", "Hindi", "Marathi", "Spanish"],
                // },

                {
                  key: "autoScroll",
                  title: "Auto Scroll",
                  description:
                    "Automatically scroll to the latest bot response.",
                  type: "toggle",
                },
              ].map(
                ({ key, title, description, type, options }, index, arr) => (
                  <div key={key}>
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <label
                          htmlFor={key}
                          className="form-label fw-semibold mb-1"
                        >
                          {title}
                        </label>

                        <p className="small mb-0">{description}</p>
                      </div>

                      <div>
                        {type === "toggle" ? (
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={key}
                              checked={toggles[key]}
                              onChange={() => handleToggle(key)}
                              style={{
                                width: "2.25em",
                                height: "1.25em",
                              }}
                            />
                          </div>
                        ) : key === "botLang" ? (
                          <select
                            id={key}
                            className="form-select form-select-sm"
                            value="English"
                            disabled
                            style={{
                              minWidth: "130px",
                              backgroundColor: "var(--input-disable-color)",
                            }}
                          >
                            <option value="English">English</option>
                          </select>
                        ) : (
                          <select
                            id={key}
                            className="form-select form-select-sm"
                            value={preferences[key] || options[1]}
                            onChange={(e) =>
                              handleDropdownChange(key, e.target.value)
                            }
                            style={{ minWidth: "130px" }}
                          >
                            {options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {index !== arr.length - 1 && <hr className="my-3" />}
                  </div>
                ),
              )}

              {/* <hr className="my-4" /> */}

              <div className="d-flex justify-content-between align-items-center border-top pt-2 mb-3">
                <h6 className="mb-0 fw-semibold">Profile Information</h6>

                {!isEditingProfileInfo ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setIsEditingProfileInfo(true)}
                    // disabled={!userProfile}
                  >
                    Edit
                  </button>
                ) : (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleSaveProfileInfo}
                      disabled={isSavingProfileInfo}
                    >
                      {isSavingProfileInfo ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setIsEditingProfileInfo(false)}
                      disabled={isSavingProfileInfo}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="row g-3">
                {/* Role */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold mb-1">Role</label>

                  {isEditingProfileInfo ? (
                    <input
                      className="form-control rounded-3"
                      value={profileInfoDraft.role}
                      onChange={(e) =>
                        setProfileInfoDraft((prev) => ({
                          ...prev,
                          role: e.target.value,
                        }))
                      }
                      placeholder="Enter your role"
                      maxLength={120}
                    />
                  ) : (
                    <div
                      className="form-control rounded-3"
                      style={{
                        backgroundColor: "var(--input-disable-color)",
                      }}
                    >
                      {userProfile?.role || "Not available"}
                    </div>
                  )}
                </div>

                {/* Expertise */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold mb-1">
                    Expertise Level
                  </label>

                  {isEditingProfileInfo ? (
                    <>
                      <input
                        className="form-control rounded-3"
                        value={profileInfoDraft.expertise_level}
                        onChange={(e) =>
                          setProfileInfoDraft((prev) => ({
                            ...prev,
                            expertise_level: e.target.value,
                          }))
                        }
                        placeholder="e.g. Beginner, Intermediate, Advanced"
                        list="expertise_level_options"
                        maxLength={60}
                      />
                      <datalist id="expertise_level_options">
                        <option value="Beginner" />
                        <option value="Intermediate" />
                        <option value="Advanced" />
                        <option value="Expert" />
                      </datalist>
                    </>
                  ) : (
                    <div
                      className="form-control rounded-3"
                      style={{
                        backgroundColor: "var(--input-disable-color)",
                      }}
                    >
                      {userProfile?.expertise_level || "Not available"}
                    </div>
                  )}
                </div>

                {/* Active Topics */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold mb-2">
                    Active Topics
                  </label>

                  {Array.isArray(sessionProfile?.active_topics) &&
                  sessionProfile.active_topics.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {sessionProfile.active_topics.map((topic) => (
                        <span
                          key={String(topic)}
                          className="badge rounded-pill text-bg-secondary"
                          style={{
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={String(topic)}
                        >
                          {String(topic)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted small">Not available</div>
                  )}
                </div>

                {/* Language Response */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold mb-2">
                    Language Response
                  </label>

                  <div
                    className="form-control rounded-3"
                    style={{
                      backgroundColor: "var(--input-disable-color)",
                    }}
                  >
                    {preferences?.botLang || "English"}
                  </div>
                </div>

                {/* Profile Summary */}
                <div className="col-12">
                  <label className="form-label fw-semibold mb-2">
                    Profile Summary
                  </label>

                  <textarea
                    className="form-control rounded-3"
                    rows={5}
                    readOnly={!isEditingProfileInfo}
                    value={
                      isEditingProfileInfo
                        ? profileInfoDraft.overall_profile_summary
                        : userProfile?.chat_preferences?.overall_profile_summary ||
                          "Not available"
                    }
                    onChange={(e) =>
                      setProfileInfoDraft((prev) => ({
                        ...prev,
                        overall_profile_summary: e.target.value,
                      }))
                    }
                    style={{
                      backgroundColor: isEditingProfileInfo
                        ? undefined
                        : "var(--input-disable-color)",
                      resize: "none",
                      lineHeight: 1.4,
                      maxHeight: "180px",
                      overflowY: "auto",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Section */}
        <div className="col-12 col-md-6 mb-4 security-card">
          <div className="card profile shadow-sm p-4 h-100">
            <h5 style={{ fontSize: "large" }} className="mb-4 fw-semibold">
              Security
            </h5>

            {/* New Password */}
            <div className="mb-4">
              <label className="form-label fw-semibold">New Password</label>
              <div className="input-group">
                <input
                  type={passwordVisibility.newPassword ? "text" : "password"}
                  name="newPassword"
                  className="form-control"
                  placeholder="Enter new password"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
                <span
                  className="input-group-text cursor-pointer"
                  onClick={() => togglePasswordVisibility("newPassword")}
                >
                  {passwordVisibility.newPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-2">
              <label className="form-label fw-semibold">Confirm Password</label>
              <div className="input-group">
                <input
                  type={
                    passwordVisibility.confirmPassword ? "text" : "password"
                  }
                  name="confirmPassword"
                  className="form-control"
                  placeholder="Re-enter new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <span
                  className="input-group-text cursor-pointer"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                >
                  {passwordVisibility.confirmPassword ? (
                    <FaEyeSlash />
                  ) : (
                    <FaEye />
                  )}
                </span>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mt-5 save-actions">
              {/* Discard Button */}
              <button
                className="rounded-5 px-5 py-2 d-flex align-items-center justify-content-center"
                style={{
                  border: "1px solid #ccc",
                  backgroundColor: "#f8f9fa",
                  color: "#333",
                  transition: "0.2s",
                }}
                onClick={() => {
                  const isEmpty =
                    !formData.newPassword.trim() &&
                    !formData.confirmPassword.trim();

                  if (isEmpty) {
                    showInfoToast("Nothing to discard");
                  } else {
                    // Clear password fields only
                    setFormData({
                      ...formData,
                      newPassword: "",
                      confirmPassword: "",
                    });
                    showInfoToast("Changes discarded");
                  }

                  setIsEditing(false);
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e9ecef")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f8f9fa")
                }
              >
                Cancel
                {/* <img
                  src={DiscardIcon}
                  alt="Discard"
                  className="ms-2 profile-ic"
                  style={{ width: "16px" }}
                /> */}
              </button>

              {/* Save Button */}
              <button
                className="rounded-5 custom-btn py-2.5 d-flex align-items-center justify-content-center"
                onClick={handleSave}
                style={{
                  border: "none",
                  backgroundColor: "#007bff",
                  color: "white",
                  transition: "0.2s",
                  paddingLeft: "3.5rem",
                  paddingRight: "3.5rem",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0069d9")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#007bff")
                }
              >
                Save
                {/* <img
                  src={SaveIcon}
                  alt="Save"
                  className="ms-2 logout-icon"
                  style={{ width: "16px" }}
                /> */}
              </button>
            </div>
          </div>
        </div>

        {/* Login Activity */}
        <div
          className={`col-12 ${
            userRole === "Admin" || userRole === "Superadmin"
              ? "col-md-12"
              : "col-md-6"
          } mb-4`}
        >
          <div className="card profile shadow-sm p-4 h-100">
            {/* Title & Description */}
            <h5 style={{ fontSize: "large" }} className="fw-semibold mb-1">
              Login Activity
            </h5>
            <p className="mb-3" style={{ fontSize: "0.9rem" }}>
              View recent user activity, device info, and last login sessions.
            </p>

            <hr className="my-2" />

            {/* Table Wrapper */}
            <div
              className="table-responsive"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                overflowX: "auto",
                borderRadius: "8px",
              }}
            >
              <table className="table table-sm table-hover align-middle mb-0">
                {/* Table Head */}
                <thead
                  className="table-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <tr>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Device
                    </th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>IP</th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Last Active
                    </th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Status
                    </th>
                    <th
                      style={{ fontWeight: 600, fontSize: "0.9rem" }}
                      className="text-center"
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {/* Show Loader */}
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        Loading activity...
                      </td>
                    </tr>
                  ) : activitySessions.length > 0 ? (
                    activitySessions.map((session) => (
                      <tr key={session.session_id}>
                        {/* Device Info */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {session.device_info
                            ? session.device_info.charAt(0).toUpperCase() +
                              session.device_info.slice(1)
                            : session.device
                              ? session.device.charAt(0).toUpperCase() +
                                session.device.slice(1)
                              : "Unknown"}
                        </td>

                        {/* IP Address */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {session.ip_address || "N/A"}
                        </td>

                        {/* Last Active */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {/* {new Date(session.created_timestamp).toLocaleString()} */}
                          {formatDateToUserTimeZone(session.created_timestamp)}
                        </td>

                        {/* Status Badge */}
                        <td>
                          <span
                            className={`badge ${
                              session.is_active
                                ? "bg-success text-white"
                                : "bg-secondary text-white"
                            }`}
                            style={{
                              fontSize: "0.75rem",
                              padding: "6px 10px",
                              borderRadius: "6px",
                            }}
                          >
                            {session.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Action Button */}
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle", // ✅ centers vertically within the row
                          }}
                        >
                          {session.is_active ? (
                            <div className="d-flex justify-content-center">
                              <button
                                onClick={() =>
                                  handleLogoutDevice(session.session_id)
                                }
                                className="btn btn-sm btn-outline-dark custom-logout-btn d-flex align-items-center justify-content-center"
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  fontSize: "0.8rem",
                                  display: "inline-flex",
                                }}
                              >
                                <span className="refresh-prompts">Logout</span>
                                <img
                                  src={RedLogoutIcon}
                                  alt="Logout"
                                  className="ms-1 icon-image"
                                  style={{ width: "14px", height: "14px" }}
                                />
                              </button>
                            </div>
                          ) : (
                            <span
                              className="text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    // No Sessions Found
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No login sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Chats History - only visible for User role */}
      {userRole === "User" && (
        <div className="col-12 mb-4">
          {/* Wrapper: ensures header hides when section scrolls out */}
          <div
            className="card profile shadow-sm card1"
            style={{
              borderRadius: "8px",
              overflow: "hidden", // ✅ Prevents chat items from showing above header
            }}
          >
            {/* Scrollable area */}
            <div
              className="p-0"
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                position: "relative",
                // background: "var(--bs-body-bg)",
              }}
            >
              {/* Sticky Header */}
              <div
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "var(--input-bg-color)",
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                <h5
                  className="fw-semibold mb-0"
                  style={{ fontSize: "1.05rem" }}
                >
                  Chat History
                </h5>
              </div>

              {/* Scrollable List */}
              <ul className="list-unstyled mb-0">
                {chatSessions.map((session) => {
                  const titleText = session.first_input || "No input";

                  return (
                    <li
                      key={session.session_id}
                      onClick={() => {
                        // Navigate to chat with selected session
                        navigate("/user/new", {
                          state: { sessionId: session.session_id },
                        });

                        // Store session -> redux
                        dispatch(setSessionId(session.session_id));

                        // Restore selected model
                        if (session.model_id) {
                          dispatch(setSelectedModel(session.model_id));
                        }

                        dispatch(triggerRefresh());
                      }}
                      className="d-flex align-items-center py-2 px-4 border-bottom clickable-session"
                      style={{
                        fontSize: "0.95rem",
                        lineHeight: "1.4",
                        gap: "1rem",
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                    >
                      {/* Title */}
                      <div
                        className="chat-title-ellipsis"
                        style={{
                          flex: "1",
                          minWidth: "0",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          color: "var(--text-color)",
                        }}
                        title={titleText}
                      >
                        {titleText}
                      </div>

                      {/* Timestamp */}
                      <span
                        className="small"
                        style={{
                          flex: "0 0 auto",
                          whiteSpace: "nowrap",
                          opacity: 0.8,
                        }}
                      >
                        {session.model_generic_name}{" "}
                        {formatTimeAgo(session.latest_at)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mt-4 save-actions">
        <button
          className="rounded-5  px-3 py-2"
          style={{ border: "none" }}
          onClick={() => setIsEditing(false)}
        >
          Discard Changes <img src={DiscardIcon} className="ms-2 profile-ic" />
        </button>
        <button
          className="rounded-5 custom-btn  px-4 py-2 "
          onClick={handleSave}
          style={{ border: "none" }}
        >
          Save Changes <img src={SaveIcon} className="ms-2 logout-icon " />
        </button>
      </div> */}
    </div>
  );
};

export default UserProfileSettings;
